import { useCallback, useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowDown, Loader2 } from 'lucide-react';
import { toast } from '@zawarski/eti-ui';
import DataTable from '@components/DataTable';
import type { DataTableColumn } from '@components/DataTable';
import ListPageShell from '@components/ListPageShell';
import { LEGACY_MOBILE_SIZE, StatusTag } from '@components/rep';
import type { SortOrder } from '@components/rep';
import { REP_CHANNELS } from '@constants/index';
import { downloadAlocReport } from '@utils/documents';
import { pluralize, sortRows } from '@utils/index';
import { useAccess } from '@hooks/useAccess';
import { useCountyName } from '@hooks/useCountyName';
import { useIsMobile } from '@hooks/useIsMobile';
import { useRealtimeSubscription } from '@hooks/useRealtime';
import { useListLcertQuery } from '@services/lcertApi';
import type { Lcert } from '@/types';

const toDateOnly = (value?: string | null) => (value ? String(value).slice(0, 10) : '');

/** The legacy "Download Report" chip: white on #5C87FF, 12px/500. */
const REPORT_BUTTON_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: 4,
  border: 0,
  borderRadius: 2,
  background: '#5C87FF',
  color: '#fff',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
} as const;

/**
 * One county's letters by year in the Palmetto 3.0 list layout of the Calibration Due Dates list
 * (Figma Virtual EOC 2782:3971): the blue header ("<County> County", "N results"), then a card
 * with the Year / Status / Report table, sortable by year and status, oldest year first; a card
 * per letter on phones. Report holds a "Download Report" chip (unless Incomplete) that asks the
 * documents service for the ALOC report; a row opens the letter. State users reach it from the
 * county list (Back arrow); county users land here from the menu (navigation button). One
 * county has a letter a year, so the table is not paged.
 */
const LcertOverview = () => {
  const navigate = useNavigate();
  const { gid } = useParams();
  const isMobile = useIsMobile(LEGACY_MOBILE_SIZE);
  const { isStateUser, groupId, accessTokenId } = useAccess();
  const { titleOf, nameOf } = useCountyName();
  const countyId = isStateUser ? Number(gid) || 0 : groupId;
  const [downloading, setDownloading] = useState<number | null>(null);
  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState('pvYear');

  const {
    data = [],
    isLoading,
    isError,
    refetch,
  } = useListLcertQuery({ county: countyId, state: isStateUser }, { skip: !countyId });
  const onEvent = useCallback(() => void refetch(), [refetch]);
  useRealtimeSubscription(REP_CHANNELS.lcert, onEvent);

  // A failed load shows its message, not the last rows, which may be another county's.
  const rows = useMemo(
    () => (isError ? [] : sortRows(data, orderBy, order)),
    [data, isError, orderBy, order],
  );
  const countyTitle = titleOf(countyId) || (nameOf(countyId) ? `${nameOf(countyId)} County` : '');

  useEffect(() => {
    document.title = `${countyTitle || 'County'} letters - Palmetto REP`;
  }, [countyTitle]);

  const downloadReport = async (item: Lcert) => {
    if (downloading) return;
    setDownloading(item.pvDataID);
    try {
      await downloadAlocReport(
        {
          pvCounty: countyTitle,
          pvReviewed: item.pvReviewed,
          pvTraining: item.pvTrainingCompleted,
          pvTrained: item.pvTrainedStaffAvailable,
          pvEquipment: item.pvEquipmentChecked,
          pvDosimetry: item.pvDosimetryTestedInspected,
          pvSubmitted: toDateOnly(item.pvDateSubmitted),
          pvUserID: item.pvUserIDSubmitted || item.pvUserID,
          pvIsNonEvaluated: !!item.pvIsNonEvaluatedMedical,
          pvNonEvaluated: item.pvIsNonEvaluatedMedical ? 1 : 0,
        },
        accessTokenId,
      );
    } catch (error) {
      console.error('LcertOverview: ALOC report download failed', item.pvDataID, error);
      toast.error('Unable to download the report');
    } finally {
      setDownloading(null);
    }
  };

  // The chip sits in a row that opens the letter: its click and Enter stay with the chip.
  const reportButton = (item: Lcert) =>
    item.pvStatus !== 'Incomplete' ? (
      <button
        type='button'
        style={REPORT_BUTTON_STYLE}
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          void downloadReport(item);
        }}
        onKeyDown={(e: KeyboardEvent) => e.stopPropagation()}>
        Download Report
        {downloading === item.pvDataID ? (
          <Loader2 aria-label='Downloading' className='rep:size-3 rep:animate-spin' />
        ) : (
          <ArrowDown className='rep:size-3' />
        )}
      </button>
    ) : null;

  const columns: DataTableColumn<Lcert>[] = [
    { field: 'pvYear', headerName: 'Year' },
    {
      field: 'pvStatus',
      headerName: 'Status',
      renderCell: (row) => <StatusTag status={row.pvStatus} />,
    },
    { field: 'report', headerName: 'Report', sortable: false, renderCell: reportButton },
  ];

  const mobileCard = (item: Lcert) => (
    <div className='rep:flex rep:items-center rep:justify-between rep:gap-2'>
      <span className='rep:text-sm rep:font-semibold rep:text-[#0A0A0A]'>{item.pvYear}</span>
      <div className='rep:flex rep:items-center rep:gap-2'>
        <StatusTag status={item.pvStatus} fontSize={12} />
        {reportButton(item)}
      </div>
    </div>
  );

  const handleRequestSort = (field: string) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
  };

  const subTitle = isLoading ? 'Loading...' : isError ? '' : pluralize(rows.length, 'result');

  return (
    <div
      className='rep:flex rep:h-full rep:w-full rep:flex-col rep:overflow-hidden'
      style={{ paddingBottom: isMobile ? 56 : 48, boxSizing: 'border-box' }}>
      <ListPageShell
        title={isLoading ? 'County' : countyTitle}
        subtitle={subTitle}
        isMobile={isMobile}
        onBack={isStateUser ? () => navigate('/lcert') : undefined}>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.pvDataID}
          loading={isLoading}
          isMobile={isMobile}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          onRowClick={(row) => navigate(`/lcert/${row.pvDataID}`)}
          renderMobileCard={mobileCard}
          emptyMessage={isError ? 'Unable to load letters.' : 'No letters yet.'}
        />
      </ListPageShell>
    </div>
  );
};

export default LcertOverview;
